// StartNewRequestModal.jsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Modal,
  Grid,
  TextField,
  Button,
  InputAdornment,
  Popper,
  Autocomplete,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../../services/userContext';
import { toast } from 'react-toastify';
import debounce from 'lodash.debounce';
import CheckIcon from '@mui/icons-material/Check';
import { REQUEST_FIELD_MAX_LENGTH } from '../../app/transferbonds/shared/requestFieldLimits';

// API call to fetch states
const getStates = async (token) => {
  const baseURL = process.env.REACT_APP_BASE_URL;
  try {
    const response = await fetch(`${baseURL}Location/states`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Error fetching states: ${response.statusText}`);
    }

    const data = await response.json();
    // Map to ensure each state has only stateCd and stateName
    return data.map((state) => ({
      stateCd: state.stateCd,
      stateName: state.stateName,
    }));
  } catch (error) {
    console.error('Error fetching states:', error);
    throw error;
  }
};

// API call to fetch counties by state
const getCountiesByState = async (token, stateCd) => {
  const baseURL = process.env.REACT_APP_BASE_URL;
  try {
    const response = await fetch(`${baseURL}Location/countiesByState/${stateCd}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Error fetching counties: ${response.statusText}`);
    }

    const data = await response.json();
    // Map to ensure each county has only countyId and county
    return data.map((county) => ({
      countyId: county.countyId,
      county: county.county,
    }));
  } catch (error) {
    console.error('Error fetching counties:', error);
    throw error;
  }
};

// API call to fetch active agents by county
const getActiveAgentsByCounty = async (token, countyId) => {
  const baseURL = process.env.REACT_APP_BASE_URL;
  try {
    const response = await fetch(
      `${baseURL}RequestingTransferBond/ActiveAgentsByCounty/${countyId}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Error fetching active agents: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching active agents:', error);
    throw error;
  }
};

// API call to submit the new transfer bond request
const submitTransferBondRequest = async (token, requestData) => {
  const baseURL = process.env.REACT_APP_BASE_URL;
  try {
    const response = await fetch(`${baseURL}requestingtransferbond`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      throw new Error(`Error submitting request: ${response.statusText}`);
    }

    const data = await response.json();
    return data; // Assuming the API returns the new request ID
  } catch (error) {
    console.error('Error submitting request:', error);
    throw error;
  }
};

// Custom Popper component for Autocomplete
const CustomPopper = (props) => {
  return <Popper {...props} placement="bottom-start" />;
};

// The StartNewRequestModal component
const StartNewRequestModal = React.memo(({ open, onClose, onRequestSuccess }) => {
  const { user } = useUser(); // Access user context
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [newRequestId, setNewRequestId] = useState(null);
  const [states, setStates] = useState([]); // Hold the states from API
  const [counties, setCounties] = useState([]); // Hold the counties from API
  const [loadingStates, setLoadingStates] = useState(false); // State to track loading of states
  const [loadingCounties, setLoadingCounties] = useState(false); // State to track loading of counties
  const [countiesDisabled, setCountiesDisabled] = useState(true); // Disable county field initially
  const [isSaving, setIsSaving] = useState(false); // State to track saving process

  // New state variables for active agents data
  const [activeAgentsData, setActiveAgentsData] = useState(null); // To store numberOfAgencies and numberOfContacts
  const [loadingActiveAgents, setLoadingActiveAgents] = useState(false); // To track loading state
  const [errorActiveAgents, setErrorActiveAgents] = useState(null); // To track errors

  const navigate = useNavigate();
  const agencyRef = useRef(null); // Reference for My Agency field

  // Refs for uncontrolled inputs
  const jailRef = useRef(null);
  const courtRef = useRef(null);
  const numBondsRef = useRef(null);
  const defendantFirstNameRef = useRef(null);
  const defendantLastNameRef = useRef(null);

  // Initial form state to reset the form
  const initialFormState = {
    agency: null,
    state: null,
    county: null,
    totalAmount: '',
    submittedBy: '',
  };

  const [formState, setFormState] = useState(initialFormState);

  // Memoize agencies to avoid recalculating on every render
  const agencies = useMemo(() => {
    return (
      user?.info?.agencyDetails
        ?.filter((agency) => agency.canRequest === true) // Filter agencies with CanRequest === true
        .map((agency) => ({
          label: agency.dbaAgencyName + ' (' + agency.agencyId + ')',
          id: agency.agencyId,
        })) || []
    );
  }, [user]);

  // Automatically select agency if there's only one
  useEffect(() => {
    if (agencies.length === 1) {
      setFormState((prev) => ({
        ...prev,
        agency: agencies[0],
      }));
    }
  }, [agencies]);

  // Focus on My Agency field when modal opens
  useEffect(() => {
    if (open && agencyRef.current) {
      agencyRef.current.focus();
    }
  }, [open]);

  // Fetch states when the modal opens
  useEffect(() => {
    const fetchStates = async () => {
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        toast.warning('No authentication token found.');
        return;
      }
      setLoadingStates(true);
      try {
        const statesData = await getStates(token);
        setStates(statesData); // Set the states from API
      } catch (error) {
        toast.error('Failed to fetch states.');
      } finally {
        setLoadingStates(false);
      }
    };

    if (open) {
      fetchStates();
    }
  }, [open]);

  // Fetch counties based on selected state
  useEffect(() => {
    const fetchCounties = async () => {
      const token = localStorage.getItem('jwtToken');
      const state = formState.state;
      if (!state || !token) {
        return;
      }
      setLoadingCounties(true);
      try {
        const countiesData = await getCountiesByState(token, state.stateCd);
        setCounties(countiesData); // Set counties based on API response
        setCountiesDisabled(false); // Enable the counties dropdown
      } catch (error) {
        toast.error('Failed to fetch counties.');
      } finally {
        setLoadingCounties(false);
      }
    };

    if (formState.state) {
      fetchCounties();
    }
  }, [formState.state]);

  // Determine the selected county based on countyId
  const selectedCounty = useMemo(() => {
    return counties.find((county) => county.countyId === (formState.county?.countyId || null));
  }, [counties, formState.county]);

  // Determine if the selected state is Texas
  const selectedState = useMemo(() => {
    return states.find((state) => state.stateCd === (formState.state?.stateCd || ''));
  }, [states, formState.state]);

  const isTexas = useMemo(() => {
    return selectedState?.stateName === 'Texas';
  }, [selectedState]);

  // Fetch active agents data when county changes
  useEffect(() => {
    const fetchActiveAgents = async () => {
      const token = localStorage.getItem('jwtToken');
      const county = formState.county;

      if (!county || !token) {
        setActiveAgentsData(null);
        return;
      }

      setLoadingActiveAgents(true);
      setErrorActiveAgents(null);

      try {
        const data = await getActiveAgentsByCounty(token, county.countyId);
        setActiveAgentsData(data);
      } catch (error) {
        setErrorActiveAgents('Failed to fetch active agents data.');
      } finally {
        setLoadingActiveAgents(false);
      }
    };

    fetchActiveAgents();
  }, [formState.county]);

  // Memoized handleInputChange with debounce for potential future use
  const debouncedSetFormState = useMemo(
    () =>
      debounce((name, value) => {
        setFormState((prev) => ({
          ...prev,
          [name]: value,
        }));
      }, 300),
    []
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSetFormState.cancel();
    };
  }, [debouncedSetFormState]);

  const handleInputChange = useCallback(
    (name) => (event, newValue) => {
      if (newValue !== undefined) {
        // For Autocomplete components
        setFormState((prev) => ({
          ...prev,
          [name]: newValue,
        }));
      }
      else {
        // For TextField components
        setFormState((prev) => ({
          ...prev,
          [name]: event.target.value,
        }));
      }
    },
    []
  );

  const handleCurrencyChange = useCallback((event) => {
    const value = event.target.value;
    const formattedValue = value.replace(/[^0-9.]/g, ''); // Remove non-numeric characters except for decimal
    setFormState((prev) => ({
      ...prev,
      totalAmount: formattedValue,
    }));
  }, []);

  const handleSave = useCallback(async () => {
    // Retrieve values from refs
    const jail = jailRef.current.value.trim();
    const court = courtRef.current.value.trim();
    const numBonds = parseInt(numBondsRef.current.value, 10) || 0;
    const totalAmount = parseFloat(formState.totalAmount) || 0;
    const defendantFirstName = defendantFirstNameRef.current.value.trim();
    const defendantLastName = defendantLastNameRef.current.value.trim();
    // const submittedBy = submittedByRef.current?.value?.trim();

    // Validate required fields
    if (!formState.agency || !formState.state || !formState.county || (!formState.jail && !formState.court) || !formState.submittedBy) {
      toast.error('Please fill out all required fields.');
      return;
    }

    // Ensure number of bonds is at least 1
    if (numBonds < 1) {
      toast.error('Number of Bonds is required and must be at least 1.');
      return;
    }

    const token = localStorage.getItem('jwtToken');
    if (!token) {
      toast.warning('No authentication token found.');
      return;
    }

    const requestData = {
      agencyId: formState.agency?.id,
      state: formState.state?.stateCd,
      countyId: formState.county?.countyId,
      jail: jail,
      court: court,
      numBonds: numBonds,
      totalAmount: totalAmount,
      defendantFirstName: defendantFirstName,
      defendantLastName: defendantLastName,
      submittedBy: formState.submittedBy,
    };

    setIsSaving(true); // Disable the button

    try {
      const responseId = await submitTransferBondRequest(token, requestData);
      setNewRequestId(responseId); // Set the returned request ID
      setIsSubmitted(true); // Mark as submitted
      toast.success('Request submitted successfully.');
      onRequestSuccess(); // Call to update the grid
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Failed to submit the request.');
    } finally {
      setIsSaving(false); // Re-enable the button after saving
    }
  }, [formState, onRequestSuccess]);

  const handleViewDetails = useCallback(() => {
    if (newRequestId) {
      navigate(`/view-details?ID=${newRequestId}`);
    }
  }, [newRequestId, navigate]);

  const handleCloseModal = useCallback(() => {
    setFormState(initialFormState); // Reset the form state to initial values
    setIsSubmitted(false); // Reset the submission state
    setCounties([]); // Clear counties
    setCountiesDisabled(true); // Disable counties dropdown
    setActiveAgentsData(null); // Reset active agents data
    setErrorActiveAgents(null); // Reset error state
    // Clear refs
    if (jailRef.current) jailRef.current.value = '';
    if (numBondsRef.current) numBondsRef.current.value = '1';
    if (defendantFirstNameRef.current) defendantFirstNameRef.current.value = '';
    if (defendantLastNameRef.current) defendantLastNameRef.current.value = '';
    if (courtRef.current) courtRef.current.value = '';
    onClose(); // Call the modal close handler
  }, [onClose, initialFormState]);

  return (
    <Modal
      open={open}
      onClose={(_, reason) =>
        reason !== 'backdropClick' && reason !== 'escapeKeyDown' && handleCloseModal()
      }
      aria-labelledby="start-new-request-modal"
      aria-describedby="start-new-request-details"
    >
      <Box
        component="form" // Use form element to control submission
        autoComplete="off" // Disable browser autocomplete
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          maxHeight: '80vh',
          overflowY: 'auto',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
        }}
        onSubmit={(e) => {
          e.preventDefault(); // Prevent default form submission
          handleSave();
        }}
      >
        {isSubmitted ? (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Request Submitted
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
              Your request has been submitted. Active posting agents will be notified of a new request
              in their respective counties. They will only be provided with the pertinent information
              you've provided.
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
              From here, you can view the details of your request, which will show prospective posting
              agent quotes and a list of currently active agents, or you can close this modal and view
              these details later in the grid below.
            </Typography>
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button variant="contained" color="primary" onClick={handleViewDetails}>
                View Details
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleCloseModal}>
                Close
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                bgcolor: '#f5f5f5', // Light grey background
                textAlign: 'center', // Center the text
                p: 1, // Add padding for better spacing
                borderRadius: 1, // Slightly rounded corners
              }}
            >
              Start a New Request
            </Typography>
            {/* **Updated Instructional Bullet Points** */}
            <Box sx={{ mb: 3 }}>
              <List dense>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <CheckIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Fill out all required fields marked with * for accurate processing."
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 30 }}>
                    <CheckIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="If you know this bond will be greater than your underwriting authority, please call Bankers Surety."
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                  />
                </ListItem>
              </List>
            </Box>
            {/* **End of Updated Instructional Bullet Points** */}
            <Grid container spacing={2}>
              {/* My Agency Autocomplete */}
              <Grid item xs={12}>
                <Autocomplete
                  options={agencies}
                  getOptionLabel={(option) => option.label}
                  value={formState.agency}
                  onChange={handleInputChange('agency')}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  PopperComponent={CustomPopper}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="My Agency *"
                      required
                      error={!formState.agency}
                      helperText={!formState.agency && 'My Agency is required'}
                      fullWidth
                      inputRef={agencyRef} // Reference for focus
                      autoComplete="off" // Disable browser autocomplete
                      name="my-agency" // Unique name
                      InputLabelProps={{ shrink: true }} // Keep label at the top
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Name of Person submitting this request"
                  name="submittedBy"
                  value={formState.submittedBy}
                  onChange={handleInputChange('submittedBy')}
                  inputProps={{ maxLength: REQUEST_FIELD_MAX_LENGTH.submittedBy }}
                  required
                  error={!formState.submittedBy}
                  helperText={!formState.submittedBy && 'This field is required'}
                  fullWidth
                  autoComplete="off"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* State Autocomplete */}
              <Grid item xs={12}>
                <Autocomplete
                  options={states}
                  getOptionLabel={(option) => option.stateName}
                  value={formState.state}
                  onChange={handleInputChange('state')}
                  isOptionEqualToValue={(option, value) => option.stateCd === value.stateCd}
                  PopperComponent={CustomPopper}
                  loading={loadingStates}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State *"
                      required
                      error={!formState.state}
                      helperText={!formState.state && 'State is required'}
                      fullWidth
                      autoComplete="off" // Disable browser autocomplete
                      name="state-field-dropdown" // Unique name
                      InputLabelProps={{ shrink: true }} // Keep label at the top
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingStates ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Louisiana State Tax Alert */}
              {formState.state && formState.state.stateCd === 'LA' && (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
                    You may need to pay for state tax fees upfront. You may proceed with submitting this request.
                  </Alert>
                </Grid>
              )}

              {/* County Autocomplete */}
              <Grid item xs={12}>
                <Autocomplete
                  options={counties}
                  getOptionLabel={(option) => option.county} // Display county name
                  value={formState.county}
                  onChange={handleInputChange('county')}
                  isOptionEqualToValue={(option, value) => option.countyId === value.countyId}
                  PopperComponent={CustomPopper}
                  disabled={countiesDisabled} // Disable until state is selected
                  loading={loadingCounties} // Show loading spinner while fetching counties
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="County *"
                      required
                      error={!formState.county}
                      helperText={
                        !formState.county && 'County is required prior to selecting State'
                      }
                      fullWidth
                      autoComplete="off" // Disable browser autocomplete
                      name="county-field-dropdown" // Unique name
                      InputLabelProps={{ shrink: true }} // Keep label at the top
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCounties ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              {formState.county && (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ fontSize: '0.85rem' }}>
                    Please ensure you're familiar with the court and forfeiture rules for the specific county or state before moving forward with the request.
                  </Alert>
                </Grid>
              )}

              {activeAgentsData && selectedCounty && (
              <Grid item xs={12}>
                <Box sx={{ borderRadius: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ ml: 1, fontWeight: 'bold', fontSize: '0.80rem' }}
                  >
                    {activeAgentsData.numberOfAgencies > 0 ? (
                      <>
                        {selectedCounty.county} county has {activeAgentsData.numberOfAgencies}{' '}
                        agencies who can post. When you submit, {activeAgentsData.numberOfContacts}{' '}
                        of these agencies will be notified immediately. {activeAgentsData.numberOfContacts === 0 ? "Since no agencies are currently active, Bankers will be notified immediately." : "If you don't get a quote in a timely manner, Bankers will be notified within 15 minutes and intervene. If no quotes have acceptable fees, please give us a call and we will contact more agents for the best possible quote"}
                      </>
                    ) : (
                      <>
                        Currently, there are no agencies available to post in {selectedCounty.county} County. However, please submit your request and Bankers will be immediately notified to assist you. 
                      </>
                    )}
                  </Typography>
                </Box>
              </Grid>
            )}

              {/* Jail */}
              <Grid item xs={12}>
                <TextField
                  label="Jail"
                  name="jail"
                  inputRef={jailRef}
                  value={formState.jail}
                  onChange={handleInputChange('jail')}
                  inputProps={{ maxLength: REQUEST_FIELD_MAX_LENGTH.jail }}
                  required
                  error={!formState.jail}
                  helperText={
                    !formState.jail && 'Jail is required'
                  }
                  fullWidth
                  autoComplete="off" // Disable browser autocomplete
                  InputLabelProps={{ shrink: true }} // Keep label at the top
                />
              </Grid>

              {/* Courthouse (Uncontrolled) */}
              <Grid item xs={12}>
                <TextField
                  label="Courthouse"
                  name="court"
                  inputRef={courtRef}
                  value={formState.court}
                  onChange={handleInputChange('court')}
                  inputProps={{ maxLength: REQUEST_FIELD_MAX_LENGTH.court }}
                  fullWidth
                  autoComplete="off" // Disable browser autocomplete
                  InputLabelProps={{ shrink: true }} // Keep label at the top
                />
              </Grid>

              {/* Number of Bonds (Uncontrolled) */}
              <Grid item xs={12}>
                <TextField
                  label="Number of Bonds"
                  name="numBonds"
                  type="number"
                  defaultValue={1}
                  inputRef={numBondsRef}
                  fullWidth
                  required
                  helperText={
                    isTexas ? (
                      <Typography sx={{ fontWeight: 'bold', fontSize: '0.80rem' }}>
                        For an accurate quote in Texas, please provide the number of bonds.
                      </Typography>
                    ) : (
                      ''
                    )
                  }
                  autoComplete="off" // Disable browser autocomplete
                  InputLabelProps={{ shrink: true }} // Keep label at the top
                />
              </Grid>

              {/* Total Bond Amount (Controlled) */}
              <Grid item xs={12}>
                <TextField
                  label="Total Bond Amount"
                  name="totalAmount"
                  value={formState.totalAmount}
                  onChange={handleCurrencyChange}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  autoComplete="off" // Disable browser autocomplete
                  InputLabelProps={{ shrink: true }} // Keep label at the top
                />
              </Grid>


              {/* Defendant First Name (Uncontrolled) */}
              <Grid item xs={12}>
                <TextField
                  label="Defendant First Name"
                  name="defendantFirstName"
                  inputRef={defendantFirstNameRef}
                  inputProps={{ maxLength: REQUEST_FIELD_MAX_LENGTH.defendantFirstName }}
                  fullWidth
                  autoComplete="off" // Disable browser autocomplete
                  InputLabelProps={{ shrink: true }} // Keep label at the top
                />
              </Grid>

              {/* Defendant Last Name (Uncontrolled) */}
              <Grid item xs={12}>
                <TextField
                  label="Defendant Last Name"
                  name="defendantLastName"
                  inputRef={defendantLastNameRef}
                  inputProps={{ maxLength: REQUEST_FIELD_MAX_LENGTH.defendantLastName }}
                  fullWidth
                  autoComplete="off" // Disable browser autocomplete
                  InputLabelProps={{ shrink: true }} // Keep label at the top
                />
              </Grid>

              {/* Loading Active Agents Data */}
              {loadingActiveAgents && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography variant="body2">Loading active agents data...</Typography>
                  </Box>
                </Grid>
              )}

              {/* Error Active Agents Data */}
              {errorActiveAgents && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {errorActiveAgents}
                  </Alert>
                </Grid>
              )}
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                type="submit" // Form submission
                variant="contained"
                color="primary"
                disabled={isSaving} // Disable when saving
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
});

export default StartNewRequestModal;
